import React, {forwardRef, useEffect, useState} from 'react';
import {DateTime} from 'luxon';
import CalendarMonth from './calendarMonth';
import CalendarYears from './calendarYears';

export interface ICalendarProps {
  id?: string;
  locale?: string;
  /**
   * Show the month of the default date
   */
  defaultDate?: number;
  /**
   * Disable days in calendar
   */
  disabledDates?: number[];
  /**
   * Selected days in calendar
   */
  selectedDates?: number[];
  /**
   * In case the selected dates build up a range, intermediate days of those two dates
   */
  activeDates?: number[];
  /**
   * Minimum date to be able to select
   */
  minDate?: number;
  /**
   * Maximum date to be able to select
   */
  maxDate?: number;
  /**
   * On change function when click another day
   */
  onSelectDate?: (date: number, e?: React.MouseEvent) => void;
  className?: string;
  [others: string]: any;
}

const Calendar = forwardRef<HTMLDivElement, ICalendarProps>((props, ref) => {
  const {
    id,
    locale = typeof navigator !== 'undefined' ? navigator.language : 'en-US',
    defaultDate,
    disabledDates,
    selectedDates,
    activeDates,
    minDate,
    maxDate,
    onSelectDate,
    className,
    ...rest
  } = props;
  const [defaultDateState, setDefaultDateState] = useState<number>(defaultDate ? defaultDate : DateTime.now().valueOf());

  const [showYears, setShowYears] = useState(false);

  const toggleShowYears = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    setShowYears(!showYears);
  };

  const updateDisplayedDate = (newDisplayedDate: number) => {
    setDefaultDateState(newDisplayedDate);
  };

  const onSelectYear = (year: number, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    const _newDate = DateTime.fromMillis(defaultDateState).set({year: year}).valueOf();
    setDefaultDateState(_newDate);
    setShowYears(false);
  };

  const selectNextMonth = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    const _nextDate = DateTime.fromMillis(defaultDateState).plus({month: 1});
    updateDisplayedDate(_nextDate.valueOf());
  };

  const selectPrevMonth = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    const _prevDate = DateTime.fromMillis(defaultDateState).plus({months: -1});
    updateDisplayedDate(_prevDate.valueOf());
  };

  const renderNavigationBar = () => {
    const _actualDate = DateTime.fromMillis(defaultDateState, {locale});
    let _navigationContent = (
      <span
        data-testid={rest && rest['data-testid'] ? `${rest['data-testid']}-nav-label` : undefined}
        className="calendar-navigation-label"
      >
        {`${_actualDate.monthLong} ${_actualDate.year}`}
      </span>
    );
    if (
      ((minDate && DateTime.fromMillis(minDate).year < DateTime.now().year) || !minDate) &&
      ((maxDate && DateTime.fromMillis(maxDate).year > DateTime.now().year) || !maxDate)
    ) {
      _navigationContent = (
        <button
          type="button"
          data-testid={rest && rest['data-testid'] ? `${rest['data-testid']}-nav-label` : undefined}
          className="calendar-navigation-label"
          onClick={toggleShowYears}
        >
          {`${_actualDate.monthLong} ${_actualDate.year}`}
          <span className="material-icons right">{showYears ? 'arrow_drop_up' : 'arrow_drop_down'}</span>
        </button>
      );
    }
    return (
      <div className="calendar-navigation">
        <button
          type="button"
          data-testid={rest && rest['data-testid'] ? `${rest['data-testid']}-btn_prev` : undefined}
          className="calendar-navigation-btn_prev"
          onClick={selectPrevMonth}
        >
          <span className="material-icons">chevron_left</span>
        </button>
        {_navigationContent}
        <button
          type="button"
          data-testid={rest && rest['data-testid'] ? `${rest['data-testid']}-btn_next` : undefined}
          className="calendar-navigation-btn_next"
          onClick={selectNextMonth}
        >
          <span className="material-icons">chevron_right</span>
        </button>
      </div>
    );
  };

  useEffect(() => {
    if (defaultDate && defaultDate !== defaultDateState) {
      setDefaultDateState(defaultDate);
    }
  }, [defaultDate]);

  return (
    <div id={id} key={id} className={`calendar ${className || ''}`} {...rest}>
      {renderNavigationBar()}
      {showYears ? (
        <CalendarYears
          year={DateTime.fromMillis(defaultDateState).year}
          onSelectYear={onSelectYear}
          minDate={minDate}
          maxDate={maxDate}
          ref={ref}
        />
      ) : (
        <CalendarMonth
          locale={locale}
          date={defaultDateState}
          month={DateTime.fromMillis(defaultDateState).month}
          selectedDates={selectedDates}
          activeDates={activeDates}
          disabledDates={disabledDates}
          minDate={minDate}
          maxDate={maxDate}
          onSelectDate={onSelectDate}
        />
      )}
    </div>
  );
});

export default Calendar;